import { analyzer, iterateAst, render } from '@thinkalpha/language-services';
import type { KnownAstNode } from '@thinkalpha/language-services';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import type { IndicatorImportRefModel } from 'src/dtos/IndicatorImportRef';
import { DEFAULT_INDICATOR_KEY } from 'src/features/code-editor/constants';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import { getImportViewModelsFromRefs, getIndicatorsFromSymbols } from 'src/lib/dictionary/dictionary';
import type { AnalyzerFunction } from 'src/lib/parser';
import { modifyCallNodesByAlias } from 'src/lib/parser/modifyCallNodesByAlias';
import type { ImportsManagerModel } from 'src/models/ImportsManagerModel';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

interface ImportsManagerModelState {
    imports: IndicatorImportModel[];
    analyzers: AnalyzerFunction[];
}

@injectable()
export class ImportsManagerModelImpl extends ReactiveInjectable implements ImportsManagerModel {
    #id: string = v4();

    #implLog: Logger;

    #state: ImportsManagerModelState;

    #autoImportRejectionList = new Set<string>();

    #indicatorKey: string | readonly string[] = DEFAULT_INDICATOR_KEY;

    get indicatorKey(): string | readonly string[] {
        return this.#indicatorKey;
    }

    setIndicatorKey(key: string | readonly string[]): void {
        // An empty array would fan out to zero lookups and reject every unresolved symbol,
        // effectively disabling auto-import until the key changes again. Fall back to the default.
        const normalizedKey = Array.isArray(key) && key.length === 0 ? DEFAULT_INDICATOR_KEY : key;
        if (this.#sameIndicatorKey(this.#indicatorKey, normalizedKey)) return;
        this.#indicatorKey = normalizedKey;
        // Symbols rejected under the previous key may resolve under the new one;
        // clear the cache so users get a fresh lookup after switching contexts.
        this.#autoImportRejectionList.clear();
    }

    #sameIndicatorKey(a: string | readonly string[], b: string | readonly string[]): boolean {
        const aArr = Array.isArray(a) ? a : [a as string];
        const bArr = Array.isArray(b) ? b : [b as string];
        return aArr.length === bArr.length && aArr.every((v, i) => v === bArr[i]);
    }

    async addImportsFromRefs(refs: IndicatorImportRefModel[]): Promise<void> {
        const resolvedImports = await this.queryClient.fetchQuery({
            queryKey: ['importsManager', 'getIndicatorsFromRefs', this.#id, refs],
            queryFn: () => getImportViewModelsFromRefs(refs),
        });

        if (!resolvedImports.length) return;

        this.addImports(...resolvedImports.map((resolved) => IndicatorImportModel.fromIndicatorImport(resolved)));
    }

    async addImportsFromRefsAndFormula(refs: IndicatorImportRefModel[], formula: string): Promise<string> {
        const parsedFormula = await this.formulaService.parse(formula);

        if (!parsedFormula.root) {
            this.#implLog.warn('Failed to parse formula');
            return formula;
        }

        const unresolvedImports: IndicatorImportRefModel[] = [];
        for (const ref of refs) {
            // Don't add the import if it's already in the imports list
            if (this.imports.some((i) => i.alias === ref.alias && i.id === ref.id)) {
                continue;
            }
            unresolvedImports.push(ref);
        }

        const resolvedImports = await this.queryClient.fetchQuery({
            queryKey: ['importsManager', 'getIndicatorsFromRefs', this.#id, refs],
            queryFn: () => getImportViewModelsFromRefs(unresolvedImports),
        });

        let modifiedRoot = parsedFormula.root;
        for (const resolvedImport of resolvedImports) {
            const resolvedImportStartingAlias = resolvedImport.alias;

            let newAlias = resolvedImportStartingAlias;
            let i = 0;
            while (this.imports.some((i) => i.alias === newAlias && i.id !== resolvedImport.id)) {
                i++;
                newAlias = `${resolvedImportStartingAlias}${i}`;
            }
            if (newAlias !== resolvedImportStartingAlias) {
                modifiedRoot = modifyCallNodesByAlias(modifiedRoot, resolvedImport.symbol, resolvedImport.alias);
            }
            this.addImports(IndicatorImportModel.fromIndicatorImport({ ...resolvedImport, alias: newAlias }));
        }

        // Safety net: walk the formula AST and resolve any call symbols the supplied refs didn't cover.
        // When refs fully describe the formula, autoImportFromCode is a no-op. When it adds imports,
        // we log a warning so we can monitor how often the caller's refs are incomplete (PLAT-8894).
        // Best-effort: if stage-2 fails (e.g. network error), log and continue with stage-1 results —
        // downstream validation will surface any truly-missing imports via the confirm-errors path.
        // Delta key is `${alias}::${id}` since addImports permits multiple imports sharing an alias
        // when the underlying id differs.
        const importKeyOf = (i: IndicatorImportModel) => `${i.alias}::${i.id}`;
        const importKeysBeforeAutoImport = new Set(this.imports.map(importKeyOf));
        try {
            await this.autoImportFromCode(modifiedRoot);
        } catch (error) {
            this.#implLog.warn({
                message: 'addImportsFromRefsAndFormula: stage-2 auto-import failed; continuing with stage-1 imports.',
                error: error instanceof Error ? error : new Error(String(error)),
                suppliedRefs: refs.map((r) => ({ alias: r.alias, id: r.id })),
                formula,
            });
        }
        const addedByAutoImport = this.imports.filter((i) => !importKeysBeforeAutoImport.has(importKeyOf(i)));

        if (addedByAutoImport.length > 0) {
            this.#implLog.warn({
                message:
                    'addImportsFromRefsAndFormula: supplied refs did not cover all formula symbols; AST-walking auto-import resolved the remainder. The caller-supplied refs appear incomplete relative to the formula.',
                suppliedRefs: refs.map((r) => ({ alias: r.alias, id: r.id })),
                addedByAutoImport: addedByAutoImport.map((i) => ({
                    alias: i.alias,
                    symbol: i.symbol,
                    id: i.id,
                })),
                formula,
            });
        }

        return render(modifiedRoot);
    }

    constructor(
        @inject('FormulaService') private formulaService: ReactBindings['FormulaService'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#implLog = logger.getSubLogger({ name: `ImportsManagerModel:${this.#id}`, id: this.#id });

        this.#state = {
            imports: [],
            analyzers: this.#makeNewAnalyzerFunctions([]),
        };
    }

    get analyzers(): AnalyzerFunction[] {
        return this.#state.analyzers;
    }

    addImports(...indicatorImports: IndicatorImportModel[]): void {
        const newImports = indicatorImports.filter(
            (newImport) =>
                !this.#state.imports.some(
                    (existingImport) => existingImport.alias === newImport.alias && existingImport.id === newImport.id,
                ),
        );

        this.imports = this.#state.imports.concat(newImports);
    }

    @reacts private set imports(imports: IndicatorImportModel[]) {
        this.#state = {
            imports,
            analyzers: this.#makeNewAnalyzerFunctions(imports),
        };
    }

    get imports(): IndicatorImportModel[] {
        return this.#state.imports;
    }

    async autoImportFromCode(...nodes: KnownAstNode[]): Promise<void> {
        const importRefs: string[] = [];

        for (const rootNode of nodes) {
            for (const node of iterateAst(rootNode)) {
                if (node.type !== 'call') continue;

                const importModel = this.#state.imports.find(
                    (imported) => imported.alias.toLowerCase() === node.source.toLowerCase(),
                );
                if (importModel) continue;

                if (this.#autoImportRejectionList.has(node.source)) continue;

                importRefs.push(node.source);
            }
        }

        if (!importRefs.length) {
            return;
        }

        const keys = Array.isArray(this.#indicatorKey) ? this.#indicatorKey : [this.#indicatorKey as string];
        // Use allSettled so a transient failure on one key doesn't drop successful results from others.
        const settled = await Promise.allSettled(
            keys.map((key) =>
                this.queryClient.fetchQuery({
                    queryKey: ['importsManager', 'getIndicatorsFromSymbols', importRefs, key],
                    queryFn: () => getIndicatorsFromSymbols(importRefs, key),
                }),
            ),
        );
        const perKey = settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
        const failedKeys = settled.flatMap((r, i) => (r.status === 'rejected' ? [keys[i]] : []));
        if (failedKeys.length > 0) {
            this.#implLog.warn({
                message: 'autoImportFromCode: one or more indicator-key lookups failed; using partial results.',
                failedKeys,
            });
        }
        // Dedupe by symbol (not id) because the alias used downstream is the symbol; two indicators
        // with the same symbol but different ids would otherwise produce duplicate aliases and make
        // call resolution order-dependent. Earlier keys in `perKey` win.
        const seenSymbols = new Set<string>();
        const indicatorImports = perKey.flat().filter((ind) => {
            const symbolKey = ind.symbol.toLowerCase();
            if (seenSymbols.has(symbolKey)) return false;
            seenSymbols.add(symbolKey);
            return true;
        });

        // Skip rejection-list updates when any key query failed — refs absent from partial results
        // may have resolved under a failed key, and we don't want a transient error to poison future retries.
        if (failedKeys.length === 0) {
            const importsWhichCouldNotBeFound = importRefs.filter(
                (ref) => !indicatorImports.some((i) => i.symbol.toLowerCase() === ref.toLowerCase()),
            );
            for (const ref of importsWhichCouldNotBeFound) {
                this.#autoImportRejectionList.add(ref);
            }
        }

        this.addImports(
            ...indicatorImports.map((indicatorImport) =>
                IndicatorImportModel.fromIndicatorImport({
                    // TODO: Check for existing imports at this symbol if the alias is already taken
                    alias: indicatorImport.symbol,
                    ...indicatorImport,
                }),
            ),
        );
    }

    clear() {
        if (TRACE) {
            this.#implLog.trace('clear');
        }

        this.imports = [];
    }

    #makeNewAnalyzerFunctions(imports: IndicatorImportModel[]): AnalyzerFunction[] {
        if (TRACE) {
            this.#implLog.trace('makeNewAnalyzerFunctions', { imports });
        }

        const functionDefs = imports.map((ind) => ind.toDictionaryFunctionDef());

        return [(x) => analyzer(x, { functionDefs })];
    }

    removeImport(indicatorImport: IndicatorImportModel): void {
        if (TRACE) {
            this.#implLog.trace('removeImport', { indicatorImport });
        }

        this.imports = this.#state.imports.filter((imported) => imported !== indicatorImport);
    }

    removeUnusedImports(...nodes: KnownAstNode[]): void {
        const seenImports = new WeakSet<IndicatorImportModel>();

        for (const rootNode of nodes) {
            for (const node of iterateAst(rootNode)) {
                if (node.type !== 'call') continue;

                const importModel = this.#state.imports.find((imported) => imported.alias === node.source);
                if (importModel) {
                    seenImports.add(importModel);
                }
            }
        }

        if (TRACE) {
            this.#implLog.trace('removeUnusedImports', { seenImports });
        }

        this.imports = this.#state.imports.filter((imported) => {
            return seenImports.has(imported);
        });
    }

    setImports(imports: IndicatorImportModel[]): void {
        if (TRACE) {
            this.#implLog.trace('setImports', { imports });
        }

        this.imports = imports;
    }
}
